// src/pages/SessionRecordingPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionService, transcriptService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Video, Mic, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SessionRecordingPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const [session,    setSession]    = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loading,    setLoading]    = useState(true);

//   useEffect(() => {
//     const load = async () => {
//       try {
//         const sRes = await sessionService.getById(id);

//         // console.log('Session response:', JSON.stringify(sRes.data, null, 2));
//         // handle various response shapes
//         // const s = sRes.data?.data ?? sRes.data?.session ?? sRes.data;
//         // const s = sRes.data?.data ?? sRes.data?.session ?? sRes.data;
//         const s = sRes.data?.data?.session ?? sRes.data?.session ?? sRes.data?.data ?? sRes.data;
//         setSession(s);

//         try {
//           const tRes = await transcriptService.get(id);
//           setTranscript(tRes.data?.data ?? tRes.data);
//         } catch {
//           // transcript may not exist yet — that's fine
//         }
//       } catch (e) {
//         toast.error('Failed to load session');
//       } finally {
//         setLoading(false);
//       }
//     };
//     load();
//   }, [id]);

useEffect(() => {
  const load = async () => {
    try {
      const sRes = await sessionService.getById(id);
      // Session is nested under data.session
      const s = sRes.data?.data?.session ?? sRes.data?.session ?? sRes.data?.data ?? sRes.data;
      setSession(s);
      // Transcript is already included in the session response — no separate call needed
      if (s?.transcript) {
        setTranscript(s.transcript);
      }
    } catch (e) {
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };
  load();
}, [id]);

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
      </div>
    </DashboardLayout>
  );

  if (!session) return (
    <DashboardLayout>
      <div className="text-center py-20 text-forest-500">Session not found.</div>
    </DashboardLayout>
  );

  const isAudio     = session.sessionType === 'AUDIO';
  const hasRecording = !!session.recordingUrl;
  const scheduledAt  = session.scheduledAt || session.booking?.scheduledAt || session.createdAt;
  const formattedDate = scheduledAt
    ? new Date(scheduledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

        {/* Back */}
        <button onClick={() => navigate(-1)}
          className="text-forest-600 hover:text-forest-800 text-sm flex items-center gap-1">
          ← Back
        </button>

        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {isAudio
              ? <Mic size={20} className="text-forest-600" />
              : <Video size={20} className="text-forest-600" />}
            <h1 className="font-serif text-2xl font-semibold text-forest-900">
              {session.title || `${session.sessionType} Session Recording`}
            </h1>
          </div>
          {formattedDate && (
            <p className="text-sm text-forest-500">{session.sessionType} · {formattedDate}</p>
          )}
        </div>

        {/* Recording player */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-cream-200">
          <div className="bg-forest-900 px-4 py-3 flex items-center gap-2">
            {isAudio
              ? <Mic size={16} className="text-cream-100" />
              : <Video size={16} className="text-cream-100" />}
            <span className="text-cream-100 text-sm font-medium">
              {isAudio ? 'Audio Recording' : 'Video Recording'}
            </span>
          </div>

          {hasRecording ? (
            isAudio ? (
              <div className="p-6 bg-forest-50">
                <audio
                  controls
                  className="w-full"
                  src={session.recordingUrl}
                  onError={() => toast.error('Could not load audio')}
                >
                  Your browser does not support audio playback.
                </audio>
              </div>
            ) : (
              <video
                controls
                className="w-full max-h-[480px] bg-black"
                src={session.recordingUrl}
                onError={() => toast.error('Could not load video')}
              >
                Your browser does not support video playback.
              </video>
            )
          ) : (
            <div className="p-10 text-center text-forest-500 bg-cream-50">
              <p className="text-lg">⏳ Recording is being processed</p>
              <p className="text-sm mt-1">Check back in a few minutes. It usually takes 5–10 minutes after the session ends.</p>
            </div>
          )}
        </div>

        {/* Transcript */}
        <div className="bg-white rounded-2xl border border-cream-200 shadow-sm">
          <div className="px-6 py-4 border-b border-cream-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-forest-600" />
              <h2 className="font-serif text-lg font-semibold text-forest-900">Transcript</h2>
            </div>
            {transcript?.pdfUrl && (
              <a href={transcript.pdfUrl} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors font-medium">
                <FileText size={12} /> Download PDF
              </a>
            )}
          </div>

          <div className="px-6 py-5">
            {!transcript && (
              <p className="text-forest-400 text-sm">No transcript available yet.</p>
            )}
            {transcript && (() => {
              // Self-heal: if content exists treat as COMPLETED regardless of stuck status
              const effectiveStatus = (transcript.pdfUrl || transcript.rawText) ? 'COMPLETED' : transcript.status;
              const isCompleted  = effectiveStatus === 'COMPLETED';
              const isFailed     = effectiveStatus === 'FAILED';
              const isProcessing = !isCompleted && !isFailed;
              return (
                <>
                  {isProcessing && (
                    <div className="flex items-center gap-2 text-forest-500 text-sm">
                      <Loader2 size={14} className="animate-spin" />
                      Transcript is being generated, check back in a few minutes...
                    </div>
                  )}
                  {isFailed && (
                    <div className="space-y-2">
                      <p className="text-red-500 text-sm">❌ Transcript generation failed.</p>
                      <p className="text-xs text-forest-400">The recording may be too short or contain no speech. Contact support if this persists.</p>
                    </div>
                  )}
                  {isCompleted && transcript.rawText && (
                    <div className="prose prose-sm max-w-none text-forest-700 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
                      {transcript.rawText}
                    </div>
                  )}
                  {isCompleted && !transcript.rawText && (
                    <p className="text-forest-400 text-sm">Transcript completed but no text was extracted.</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}